import {Show} from 'platform/foundation';

import {type CSSProperties} from 'react';

import {
  BLUE_70,
  FG_PRIMARY,
  FG_SECONDARY,
  FG_TERTIARY,
  NEUTRAL_20,
  NEUTRAL_30,
  ELEV_1,
} from '../../constants';

export type SegmentOption<T extends string | number> = {value: T; label: string};

// Static base style shared by every segment button; the active/inactive
// appearance is layered on top via getSegmentButtonStyle.
const SEGMENT_BUTTON_BASE_STYLE: CSSProperties = {
  flex: '1 1 0',
  minWidth: 0,
  height: '30px',
  // Tighter than the mock's 12.5px / 0 6px so all five real height
  // options (incl. "Thumbnail") fit the 360px panel without truncating.
  padding: '0 2px',
  border: 0,
  borderRadius: '3px',
  fontSize: '12px',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  transition: 'background 120ms, color 120ms, box-shadow 120ms',
};

function getSegmentButtonStyle(isActive: boolean): CSSProperties {
  return {
    ...SEGMENT_BUTTON_BASE_STYLE,
    ...(isActive
      ? {background: '#FFFFFF', color: BLUE_70, boxShadow: ELEV_1, fontWeight: 600}
      : {background: 'transparent', color: FG_SECONDARY, fontWeight: 500}),
  };
}

export function SegmentedControl<T extends string | number>(props: {
  label: string;
  options: SegmentOption<T>[];
  value: T;
  onChange: (value: T) => void;
  hint?: string;
}) {
  return (
    <div style={{marginBottom: '16px'}}>
      <div style={{fontSize: '13px', fontWeight: 500, color: FG_PRIMARY, marginBottom: '7px'}}>
        {props.label}
      </div>
      <div
        role="radiogroup"
        aria-label={props.label}
        style={{
          display: 'flex',
          gap: '3px',
          padding: '3px',
          background: NEUTRAL_20,
          borderRadius: '4px',
          border: `1px solid ${NEUTRAL_30}`,
        }}
      >
        {props.options.map((option) => {
          const isActive = option.value === props.value;
          return (
            <button
              key={String(option.value)}
              type="button"
              role="radio"
              aria-checked={isActive}
              aria-label={option.label}
              onClick={() => props.onChange(option.value)}
              style={getSegmentButtonStyle(isActive)}
            >
              {option.label}
            </button>
          );
        })}
      </div>
      <Show when={props.hint != null}>
        <div style={{fontSize: '12px', color: FG_TERTIARY, marginTop: '6px', lineHeight: 1.4}}>
          {props.hint}
        </div>
      </Show>
    </div>
  );
}
